import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ARTICLE_POSTS, CATEGORIES } from '../data/ArticlesData';
import Reveal from './ui/Reveal';
import ShinyText from './reactbits/ShinyText/ShinyText';
import SpotlightCard from './reactbits/SpotlightCard/SpotlightCard';

const useInitEffects = () => {
  useEffect(() => {
    if (window.WOW) new window.WOW().init();
    if (window.initMagnetic) window.initMagnetic();
  }, []);
};

const ArticlesMain = () => {
  useInitEffects();

  /* ------------- STATE ------------- */
  const POSTS_PER_PAGE = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setCategory] = useState('All');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  /* ------------- FILTER + PAGINATE ------------- */
  const filteredPosts = useMemo(() => {
    const normalized = searchQuery.trim().toLowerCase();
    const searched = normalized
      ? ARTICLE_POSTS.filter(
          (post) =>
            post.title.toLowerCase().includes(normalized) ||
            post.description.toLowerCase().includes(normalized) ||
            post.category.toLowerCase().includes(normalized)
        )
      : ARTICLE_POSTS;

    return activeCategory === 'All'
      ? searched
      : searched.filter((p) => p.category === activeCategory);
  }, [activeCategory, searchQuery]);

  const totalPages     = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  /* ------------- HANDLERS ------------- */
  const handleCategoryClick = (cat) => {
    setCategory(cat);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchQuery(searchInput.trim());
    setCurrentPage(1);
  };

  const gotoPage = (page) => setCurrentPage(page);

  /* ------------- RENDER ------------- */
  return (
    <>
      <div className="home3-blog-area sec-mar">
        <div className="container">
          <div className="row g-lg-4 gy-5">
            {/* ------------- BLOG GRID ------------- */}
            <div className="col-lg-8">
              <div className="row g-lg-4 gy-5">
                {paginatedPosts.length > 0 ? (
                  paginatedPosts.map((post) => {
                    return (
                      <div
                        key={post.id}
                        className={`col-md-6 wow animate ${post.wowDir}`}
                        data-wow-delay={post.wowDelay}
                        data-wow-duration="1500ms"
                      >
                        <SpotlightCard className="single-blog magnetic-item nalexus-spotlight" spotlightColor="rgba(223, 169, 37, 0.3)">
                          <div className="blog-img">
                            <img className="img-fluid" src={post.img} alt={post.title} />
                            <div className="blog-tag">
                              <Link to="/articles">{post.category}</Link>
                            </div>
                          </div>
                          <div className="blog-content">
                            <ul className="blog-meta">
                              <li>{post.date}</li>
                            </ul>
                            <h4>
                            <Link to={`/articles/${post.id}`}>{post.title}</Link>
                            </h4>
                            <p>{post.description}</p>
                            <div className="blog-footer">
                              <div className="read-btn">
                              <Link to={`/articles/${post.id}`}>
                                  Read More
                                  <svg width="12" height="12" viewBox="0 0 13 13" fill="none">
                                    <path d="M0 1H12M12 1V13M12 1L0.5 12" />
                                  </svg>
                                </Link>
                              </div>
                              <span className="author-tag">By {post.author}</span>
                            </div>
                          </div>
                        </SpotlightCard>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center">No articles matched your filters.</p>
                )}
              </div>

              {/* ------------- PAGINATION ------------- */}
              {totalPages > 1 && (
                <div className="row">
                  <nav aria-label="Blog pagination">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <a
                          href="#!"
                          className="page-link"
                          onClick={(e) => { e.preventDefault(); gotoPage(currentPage - 1); }}
                        >
                          <i className="bi bi-arrow-left" />
                        </a>
                      </li>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                        <li key={pg} className="page-item">
                          <a
                            href="#!"
                            className={`page-link ${currentPage === pg ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); gotoPage(pg); }}
                          >
                            {pg}
                          </a>
                        </li>
                      ))}

                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <a
                          href="#!"
                          className="page-link"
                          onClick={(e) => { e.preventDefault(); gotoPage(currentPage + 1); }}
                        >
                          <i className="bi bi-arrow-right" />
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>

            {/* ------------- SIDEBAR ------------- */}
            <div className="col-lg-4">
              <div className="widget-area">
                <SpotlightCard className="single-widgets widget_search nalexus-spotlight" spotlightColor="rgba(223, 169, 37, 0.25)">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="wp-block-search__inside-wrapper">
                      <input
                        type="search"
                        className="wp-block-search__input"
                        placeholder="Search articles..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                      <button type="submit" className="wp-block-search__button primary-btn3 nalexus-cta">
                        Search
                      </button>
                    </div>
                  </form>
                </SpotlightCard>

                {/* ---- Category filter widget ---- */}
                <SpotlightCard className="single-widgets widget_egns_categoris nalexus-spotlight" spotlightColor="rgba(223, 169, 37, 0.25)">
                  <div className="widget-title"><h4>Categories</h4></div>
                  <ul className="wp-block-categoris-cloud">
                    {CATEGORIES.map((cat) => (
                      <li key={cat}>
                        <a
                          href="#!"
                          className={activeCategory === cat ? 'active' : ''}
                          onClick={(e) => { e.preventDefault(); handleCategoryClick(cat); }}
                        >
                          <span>{cat}</span>
                          <span className="number-of-categoris">
                            ({cat === 'All'
                              ? ARTICLE_POSTS.length
                              : ARTICLE_POSTS.filter((p) => p.category === cat).length})
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </SpotlightCard>

                {/* ---- recent posts widgets ---- */}
                <SpotlightCard className="single-widgets widget_egns_recent_post nalexus-spotlight" spotlightColor="rgba(223, 169, 37, 0.25)">
                  <div className="widget-title"><h4>Recent Insights</h4></div>
                  <div className="recent-post-wraper">
                    {ARTICLE_POSTS.slice(-4).reverse().map((post) => (
                      <div key={post.id} className="widget-cnt">
                        <div className="wi">
                          <Link to={`/articles/${post.id}`}>
                            <img src={post.img} alt={post.title} />
                          </Link>
                        </div>
                        <div className="wc">
                          <h6><Link to={`/articles/${post.id}`}>{post.title}</Link></h6>
                          <Link to={`/articles/${post.id}`}>{post.date}</Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </SpotlightCard>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="blog-banner sec-mar">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <Reveal className="blog-banner-wrap" delay={0.08}>
                <div className="banner-img">
                  <img src="/assets/img/banners/vector_img2.png" alt="Blog banner" />
                </div>
                <div className="banner-content">
                  <h2>
                    <ShinyText
                      text="Articles"
                      className="nalexus-heading-shine"
                      color="#f1ede3"
                      shineColor="#ffd369"
                      speed={2.6}
                    />
                  </h2>
                  <p>Practical writing on software, automation, AI systems, and dependable digital operations.</p>
                  <Link className="sync-button" to="/contact">Discuss a system</Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArticlesMain;





